/* eslint-disable @typescript-eslint/no-explicit-any */
// ============= Imports =============
import { FormData, SectionKey } from '../../../../types';
import {
  FormState,
  SectionProgress,
} from '../../../container/forms/types/core/form-state.types';
import { PaymentWorkflowType } from '../../../container/forms/types/api/graph.types';
import { createLogger } from '../../../../utils/logger';
import { normalizeError, getResponseFromError } from './errorUtils';
import {
  determineWorkflowType,
  mapSectionKey,
  determineCurrentSection,
  createInitialFormStateForWorkflow,
  isMultiSectionWorkflow,
} from './workflowHelpers';
import { mapWorkflowSectionToFormState } from './mapWorkflowSectionsToForm';
import {
  buildSectionSubmitPayload,
} from './mapFormSectionsToWorkflow';

// ============= Logger =============
const logger = createLogger('WorkflowMapper');

// ============= Main Mapper: Workflow → Form =============

/**
 * Maps API workflow data (FormData) into UI form state (FormState).
 *
 * **Flow:**
 * 1. **Workflow type** – Inferred from `workflowData.workflowType`, `workflowData.name`, or section keys.
 * 2. **Initial form state** – Created via `createInitialFormStateForWorkflow(workflowType)` (multi- or single-section).
 * 3. **Base state** – Id, name, status, dates, assignee, and **current section** are set. Current section is derived from
 *    `workflowData.status` (mapped to a section key), or from the first incomplete section, or the last section if all complete.
 * 4. **Per-section mapping** – For each item in `workflowData.sections`:
 *    - The API section key is mapped to an internal `SectionKey` via `WORKFLOW_SECTION_MAP`.
 *    - Section-specific logic (by workflow type and section key) maps `fields`, `gridData`, and `documents` into `formState.data`.
 *    - For multi-section workflows, `sectionProgress`, `sectionValidation`, and `completedSections` are updated from each section’s `isComplete` and `completedDate`.
 * 5. **Error handling** – On failure, errors are normalized and logged with optional response/body for API parsing; the error is rethrown so callers can use the **Critical** pattern (setError + handleError).
 *
 * @param workflowData – Raw workflow payload from the API.
 * @returns Fully populated FormState for the form UI.
 * @throws Error when mapping fails (e.g. invalid data); callers should catch and call handleError(..., response).
 */
export const mapWorkflowToFormData = (workflowData: FormData): FormState => {
  try {
    logger.debug('Starting workflow data mapping', {
      workflowData,
      sections: workflowData.sections?.map((s) => ({
        sectionKey: s.sectionKey,
        isComplete: s.isComplete,
        fields: s.fields,
        gridData: s.gridData,
      })),
    });

    const workflowType = determineWorkflowType(workflowData);
    logger.debug('Determined workflow type', { workflowType });

    const initialFormState = createInitialFormStateForWorkflow(workflowType);
    logger.debug('Created initial form state', { initialFormState });

    const formState: FormState = {
      ...initialFormState,
      workflowType,
      office: initialFormState.office || 'UX',
      errors: initialFormState.errors || {},
      isSubmitting: initialFormState.isSubmitting ?? false,
      submittedSections: initialFormState.submittedSections ?? [],
      isDirty: initialFormState.isDirty || false,
      isComplete: initialFormState.isComplete || false,
      completedSections: initialFormState.completedSections || [],
      sectionValidation:
        initialFormState.sectionValidation ||
        ({} as Record<SectionKey, boolean>),
      sectionProgress:
        initialFormState.sectionProgress || ({} as SectionProgress),
      isLoading: initialFormState.isLoading || true,
      isRejected:
        initialFormState.isRejected || workflowData.status === 'Rejected',
      isCancelled:
        initialFormState.isCancelled || workflowData.status === 'Cancelled',
      data: {
        ...initialFormState.data,
        id: workflowData.id,
        name: workflowData.name,
        currentAuthStage: workflowData.status || initialFormState.currentSection,
        status: workflowData.status,
        sections: workflowData.sections,
        createdDate: workflowData.createdDate,
        createdBy: workflowData.createdBy,
        createdByEmail: workflowData.createdByEmail,
        createdByName: workflowData.createdByName,
        submittedDate: workflowData.submittedDate,
        currentlyAssignedTo:
          workflowData.sections.find((s) => s.assignedToUserId)
            ?.assignedToUserId || '',
      },
      currentSection: determineCurrentSection(workflowData, workflowType),
    };

    workflowData.sections.forEach((section) => {
      try {
        logger.debug('Processing section', {
          originalSectionKey: section.sectionKey,
          workflowType,
          sectionFields: section.fields,
          sectionGridData: section.gridData,
          sectionDocuments: section.documents,
        });

        const mappedSectionKey = mapSectionKey(
          section.sectionKey,
          workflowType
        );
        logger.debug('Mapped section key', {
          original: section.sectionKey,
          mapped: mappedSectionKey,
        });

        mapWorkflowSectionToFormState(
          {
            sectionKey: section.sectionKey,
            fields: section.fields || {},
            gridData: section.gridData,
            documents: section.documents,
          },
          workflowType,
          formState
        );

        if (isMultiSectionWorkflow(workflowType)) {
          (formState.sectionProgress as any)[mappedSectionKey] = {
            isComplete: section.isComplete,
            completedDate: section.completedDate || undefined,
          };
          if (section.isComplete) {
            formState.sectionValidation[mappedSectionKey] = true;
            formState.completedSections = Array.from(
              new Set([
                ...formState.completedSections,
                mappedSectionKey as SectionKey,
              ])
            );
          }
        }
      } catch (err) {
        const error = normalizeError(err, 'Error processing section');
        const response = getResponseFromError(err);
        logger.error('Error processing section', {
          sectionKey: section.sectionKey,
          error: error.message,
          stack: error.stack,
          response,
        });
      }
    });

    logger.debug('Final mapped form state', {
      formState,
      dataKeys: Object.keys(formState.data),
      dataSample: Object.fromEntries(
        Object.entries(formState.data).slice(0, 10)
      ),
      workflowType,
      sectionCount: workflowData.sections.length,
    });

    return formState;
  } catch (err) {
    const error = normalizeError(err, 'Failed to map workflow data to form');
    const response = getResponseFromError(err);
    logger.error('Error mapping workflow data', {
      error,
      message: error.message,
      response,
      workflowId: workflowData?.id,
    });
    throw error;
  }
}

// ============= Main Mapper: Form → API submit payload =============

/**
 * Builds the workflow API submit payload from the current form state.
 *
 * **Call chain (submit):**
 * `useFormHandlers.handleSubmit`
 *   → `buildWorkflowSubmitPayload` (this function)
 *     → `buildSectionSubmitPayload` (per workflow type + current section)
 *       → e.g. `buildOfficeAccClientMatterApiFields` for client/matter codes
 *   → `submitWorkflow` POST with `{ fields, gridData, documents }`
 *
 * @param formState – Full form state (workflowType, currentSection, data, etc.).
 * @returns Partial FormData with one section, ready for submit/update.
 */
export const buildWorkflowSubmitPayload = (
  formState: FormState
): Partial<FormData> => {
  try {
    const workflowType =
      formState.workflowType || PaymentWorkflowType.Undefined;
    const sectionKey =
      workflowType === PaymentWorkflowType.DubaiPaymentApproval ||
      workflowType === PaymentWorkflowType.DubaiPaymentFromOfficeAcc
        ? formState.currentSection
        : formState.currentSection || 'payment_details';

    const payload = buildSectionSubmitPayload(formState);
    const sectionProgress = formState.sectionProgress as any;
    const currentSectionState = sectionProgress[formState.currentSection];

    const currentSection = {
      sectionKey,
      isComplete: currentSectionState?.isComplete ?? false,
      completedDate: currentSectionState?.completedDate ?? null,
      assignedToUserId: formState.data.currentlyAssignedTo ?? null,
      fields: payload.fields,
      gridData: payload.gridData,
      documents: payload.documents,
    };

    const formData: Partial<FormData> = {
      id: formState.data.id,
      name: formState.data.name,
      status: formState.currentSection,
      createdDate: formState.data.createdDate,
      submittedDate: formState.data.submittedDate,
      sections: [currentSection as unknown as FormData['sections'][number]],
    };

    return formData;
  } catch (err) {
    const error = normalizeError(err, 'Failed to map form data to workflow');
    const response = getResponseFromError(err);
    logger.error('Error mapping form to workflow data', {
      error,
      message: error.message,
      response,
      workflowId: formState?.data?.id,
    });
    throw error;
  }
};

/** @deprecated Use buildWorkflowSubmitPayload */
export const mapFormToWorkflowData = buildWorkflowSubmitPayload;
